/**
 * MyWeekEditScheduleDialog — the small loader that lets My week's per-
 * line "Edit schedule" link (and the capacity map's SPICE Assign) open
 * the SAME schedule editor the client record uses
 * (SpiceScheduleEditDialog), from a page that has no client-record
 * aggregate to read the current plan off of. The editor answers to
 * canEditClientSchedule itself.
 *
 * The home page's own aggregate (cmHome) intentionally carries only the
 * facts My week needs to render a line (client, session time, hold,
 * last plan), not the full schedule shape (every session with its kind,
 * frequency and starting week) the edit dialog needs to prefill. Rather
 * than widen the home read for an action taken on maybe one line a day,
 * this loader fetches the standalone GET .../spice-plan endpoint (gated
 * read_clinical_record — the same floor the home page itself opens on)
 * only when the dialog is actually opened. The GET's top-level
 * therapist_roster_id is the client's own therapist, which each
 * session's Therapist select names as its default.
 */

import { type ReactNode } from 'react';
import { spicePlanToVitals, useClientSpicePlan, type SpiceScheduleVitals } from '@/lib/spiceVisitPlansApi';
import { SpiceScheduleEditDialog } from '@/components/clientRecord/VitalsBand';

export interface MyWeekEditScheduleDialogProps {
  tenantSlug: string;
  clientId: string;
  /** The date of the visit the editor was opened from ('YYYY-MM-DD'): a
   *  session switched to Monthly takes its week of the month from it. */
  initialDate?: string;
  onClose: () => void;
}

export function MyWeekEditScheduleDialog({
  tenantSlug,
  clientId,
  initialDate,
  onClose,
}: MyWeekEditScheduleDialogProps): ReactNode {
  const { data, isLoading, isError } = useClientSpicePlan(tenantSlug, clientId);

  const plan: SpiceScheduleVitals | null = data?.plan ? spicePlanToVitals(data.plan) : null;

  // A loading/error beat renders as a minimal inline note rather than
  // the full dialog chrome popping in twice (once empty, once filled) —
  // this fetch is small and gated behind an explicit click, so the wait
  // is brief either way.
  if (isLoading) {
    return (
      <div role="status" data-testid="my-week-edit-schedule-loading" className="sr-only">
        Loading schedule…
      </div>
    );
  }
  if (isError) {
    // Open the editor anyway on sensible defaults rather than block a
    // schedule change just because the prefill read failed; the save
    // path re-validates everything server-side.
    return (
      <SpiceScheduleEditDialog
        plan={null}
        onClose={onClose}
        clientId={clientId}
        tenantSlug={tenantSlug}
        spiceTherapistRosterId={null}
        initialDate={initialDate}
      />
    );
  }

  return (
    <SpiceScheduleEditDialog
      plan={plan}
      onClose={onClose}
      clientId={clientId}
      tenantSlug={tenantSlug}
      spiceTherapistRosterId={data?.therapist_roster_id ?? null}
      initialDate={initialDate}
    />
  );
}
